import {useState} from 'react';
import {useIntl} from 'react-intl';
import classNames from 'classnames';
import {FixedSizeList} from 'react-window';

import {InputSearch} from '@time-webkit/all/molecules/input-search';
import {InputSearchSize} from '@time-webkit/all/molecules/input-search/src';
import {BorderRadiusSize, Dropdown} from '@time-webkit/all/organisms/dropdown';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';
import {filterCountriesBySearch, sortCountries} from '@time-webkit/all/utils/phone-codes/helpers';
import {COUNTRIES, type Country} from '@time-webkit/all/utils/phone-codes/constants';

import {Row} from './row';

import styles from './styles.module.css';

type Props = {
    open?: boolean;
    targetRef: React.RefObject<HTMLElement>;
    onSelectCountry: (country: Country) => void;
    onClose?: () => void;
};

const ITEM_SIZE = 36;
const MAX_HEIGHT_COUNTRIES_LIST = ITEM_SIZE * 5;

const SORTED_COUNTRIES = sortCountries(Object.values(COUNTRIES));

const styleForFixedSizeList: React.CSSProperties = {overflowY: 'scroll'};

export const CountriesDropdown = ({open, targetRef, onSelectCountry, onClose}: Props) => {
    const [searchValue, setSearchValue] = useState('');

    const [noMatchesCls] = useTypography([{size: TypographySize.BodyS}]);
    const {formatMessage} = useIntl();

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose?.();
        }
    };

    const handleCountryClick = (country: Country) => {
        onSelectCountry(country);
        onClose?.();
    };

    const handleSearchClear = () => {
        setSearchValue('');
    };

    const finalCountries = filterCountriesBySearch(SORTED_COUNTRIES, searchValue);

    return (
        <div
            onClick={(e) => e.stopPropagation()}
            onKeyPress={handleKeyPress}
        >
            <Dropdown
                targetRef={targetRef}
                open={open}
                onClose={onClose}
                shouldMirrorWidth={true}
                shouldFlip={true}
                maxHeight={236}
                borderRadiusSize={BorderRadiusSize.Large}
            >
                <div className={styles.dropdown_container}>
                    <div className={styles.search_container}>
                        <InputSearch
                            placeholder={formatMessage({
                                id: 'phone_input.country_or_code',
                                defaultMessage: 'Country or code',
                            })}
                            className={styles.search}
                            value={searchValue}
                            onChange={setSearchValue}
                            size={InputSearchSize.Small}
                            onClear={handleSearchClear}
                        />
                    </div>
                    {finalCountries.length > 0 && (
                        <FixedSizeList
                            itemCount={finalCountries.length}
                            className={styles.list}
                            itemData={{
                                handleCountryClick,
                                finalCountries,
                            }}
                            width='100%'
                            height={Math.min(finalCountries.length * ITEM_SIZE, MAX_HEIGHT_COUNTRIES_LIST)}
                            itemSize={ITEM_SIZE}
                            style={styleForFixedSizeList}
                        >
                            {Row}
                        </FixedSizeList>
                    )}
                    {finalCountries.length === 0 && (
                        <div className={classNames(noMatchesCls, styles.no_matches)}>
                            {formatMessage({id: 'phone_input.no_matches', defaultMessage: 'No match'})}
                        </div>
                    )}
                </div>
            </Dropdown>
        </div>
    );
};
